// src/pages/motorcycles/MotorcycleFormPage.js

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useParams } from "react-router-dom";
import {
    Alert,
    Button,
    Card,
    Col,
    Container,
    Nav,
    Row,
} from "react-bootstrap";
import { Formik } from "formik";
import API from "../../api/axios";
import useCompanyContext from "../../hooks/useCompanyContext";

import DetailTab from "./tabs/DetailTab";
import PricingTab from "./tabs/PricingTab";
import MileageTab from "./tabs/MileageTab";
import TravelTab from "./tabs/TravelTab";
import ExcessDepositTab from "./tabs/ExcessDepositTab";

import { diffById, diffObject } from "./diff";

import {
    buildRegularExtrasPayload,
    detectSeasonalPricing,
    normalizeAvailability,
    numOrNull,
    numOrZero,
    pricesStartsToRanges,
    rangesToStarts,
} from "./adapters";

import {
    buildCountryExtrasPayload,
    buildCountryServicesInitialValues,
    createEmptyCountryServices,
    validateCountryServices,
} from "./utils/countryExtras";

import { normalizeRangesSave } from "./utils/tiers";

import "../../assets/styles/MotorcycleForm.css";

const uniqueIds = (ids) => [
    ...new Set(
        (ids || [])
            .map(String)
            .filter(Boolean)
    ),
];

const emptyMileageServices = () => ({
    freeUnlimitedAfterDays: {
        enabled: false,
        minRentalDays: "",
    },

    unlimitedPerDay: {
        enabled: false,
        pricePerDay: "",
        isRequired: false,
    },

    limitedKmPerDay: {
        enabled: false,
        pricePerDay: "",
        kilometersPerDay: "",
    },
});

const buildMileageServicesInitialValues = (
    items = []
) => {
    const result =
        emptyMileageServices();

    for (const item of Array.isArray(items)
        ? items
        : []) {
        if (
            item?.type ===
            "FREE_UNLIMITED_AFTER_DAYS"
        ) {
            result.freeUnlimitedAfterDays = {
                enabled: true,

                minRentalDays:
                    item.minRentalDays ?? "",
            };
        }

        if (
            item?.type ===
            "UNLIMITED_PER_DAY"
        ) {
            result.unlimitedPerDay = {
                enabled: true,

                pricePerDay:
                    item.pricePerDay == null
                        ? ""
                        : Number(
                            item.pricePerDay
                        ) / 100,

                isRequired: Boolean(
                    item.isRequired
                ),
            };
        }

        if (
            item?.type ===
            "LIMITED_KM_PER_DAY"
        ) {
            result.limitedKmPerDay = {
                enabled: true,

                pricePerDay:
                    item.pricePerDay == null
                        ? ""
                        : Number(
                            item.pricePerDay
                        ) / 100,

                kilometersPerDay:
                    item.kilometersPerDay ??
                    "",
            };
        }
    }

    return result;
};

const buildMileageExtrasPayload = (
    services = {}
) => {
    const result = [];

    const free =
        services.freeUnlimitedAfterDays;

    if (
        free?.enabled &&
        Number(free.minRentalDays) > 0
    ) {
        result.push({
            type:
                "FREE_UNLIMITED_AFTER_DAYS",

            isRequired: true,
            pricePerDay: 0,

            minRentalDays: Math.trunc(
                Number(free.minRentalDays)
            ),

            kilometersPerDay: null,
        });
    }

    const unlimited =
        services.unlimitedPerDay;

    if (
        unlimited?.enabled &&
        Number(unlimited.pricePerDay) > 0
    ) {
        result.push({
            type: "UNLIMITED_PER_DAY",

            isRequired: Boolean(
                unlimited.isRequired
            ),

            pricePerDay: Math.round(
                Number(
                    unlimited.pricePerDay
                ) * 100
            ),

            minRentalDays: null,
            kilometersPerDay: null,
        });
    }

    const limited =
        services.limitedKmPerDay;

    if (
        limited?.enabled &&
        Number(limited.pricePerDay) > 0 &&
        Number(
            limited.kilometersPerDay
        ) > 0
    ) {
        result.push({
            type: "LIMITED_KM_PER_DAY",

            isRequired: false,

            pricePerDay: Math.round(
                Number(
                    limited.pricePerDay
                ) * 100
            ),

            minRentalDays: null,

            kilometersPerDay: Math.trunc(
                Number(
                    limited.kilometersPerDay
                )
            ),
        });
    }

    return result;
};

const getExtraKey = (item) =>
    String(
        item?.Extra?.key ??
        item?.key ??
        ""
    )
        .trim()
        .toLowerCase();

const validateExcessDeposit = (
    values
) => {
    const rentalPolicy =
        values?.rentalPolicy || {};

    const missingStandard = [
        [
            "Security Deposit",
            rentalPolicy.deposit,
        ],
        [
            "Damage Excess",
            rentalPolicy.damageExcess,
        ],
        [
            "Theft Excess",
            rentalPolicy.theftExcess,
        ],
    ].filter(
        ([, value]) =>
            value === "" ||
            value === null ||
            value === undefined
    );

    if (missingStandard.length) {
        return `Please complete: ${missingStandard
            .map(([label]) => label)
            .join(", ")}.`;
    }

    const extras = Array.isArray(
        values?.ShopMotorcycleExtra
    )
        ? values.ShopMotorcycleExtra
        : [];

    for (const config of [
        {
            key: "cdw",
            label: "CDW",
            suffix: 1,
        },
        {
            key: "cdw2",
            label: "CDW 2",
            suffix: 2,
        },
    ]) {
        const extra = extras.find(
            (item) =>
                getExtraKey(item) ===
                config.key
        );

        if (!extra) {
            continue;
        }

        const requiredValues = [
            [
                "Price",
                Number(
                    extra.pricePerDay
                ) > 0,
            ],

            [
                "Price Type",
                Boolean(extra.priceType),
            ],

            [
                "Reduced Security Deposit",

                values[
                `discountedDepositExtra${config.suffix}`
                ] !== "" &&
                values[
                `discountedDepositExtra${config.suffix}`
                ] !== null &&
                values[
                `discountedDepositExtra${config.suffix}`
                ] !== undefined,
            ],

            [
                "Reduced Damage Excess",

                values[
                `discountedDamageExcess${config.suffix}`
                ] !== "" &&
                values[
                `discountedDamageExcess${config.suffix}`
                ] !== null &&
                values[
                `discountedDamageExcess${config.suffix}`
                ] !== undefined,
            ],

            [
                "Reduced Theft Excess",

                values[
                `discountedTheftExcess${config.suffix}`
                ] !== "" &&
                values[
                `discountedTheftExcess${config.suffix}`
                ] !== null &&
                values[
                `discountedTheftExcess${config.suffix}`
                ] !== undefined,
            ],
        ];

        const missing =
            requiredValues
                .filter(
                    ([, valid]) =>
                        !valid
                )
                .map(
                    ([label]) =>
                        label
                );

        if (missing.length) {
            return `${config.label} is enabled. Please complete: ${missing.join(
                ", "
            )}.`;
        }

        if (
            !extra.extraId &&
            !extra?.Extra?.id
        ) {
            return `${config.label} Extra definition was not found. The form meta must return an Extra with key "${config.key}".`;
        }
    }

    return "";
};

const getInternationalTravelAllowed = (
    values
) =>
    Boolean(
        values.rentalPolicy
            ?.canLeaveCity
    ) &&
    Boolean(
        values.rentalPolicy
            ?.canLeaveState
    ) &&
    Boolean(
        values.rentalPolicy
            ?.countryRestriction
    );

export default function MotorcycleFormPage() {
    const [activeTab, setActiveTab] =
        useState("detail");

    const tabs = useMemo(
        () => [
            {
                id: "detail",
                label:
                    "Basic Information",
            },
            {
                id: "travel",
                label:
                    "Travel Permissions",
            },
            {
                id: "mileage",
                label:
                    "Mileage Options",
            },
            {
                id: "excess-deposit",
                label:
                    "Deposit & Insurance",
            },
            {
                id: "prices",
                label: "Pricing",
            },
        ],
        []
    );

    const currentIndex =
        tabs.findIndex(
            (tab) =>
                tab.id === activeTab
        );

    const {
        id: motorcycleModelId,
    } = useParams();

    const isEdit = Boolean(
        motorcycleModelId
    );

    const {
        currentCompanyID,
        hasSpecificCompany,
    } = useCompanyContext();

    const supplierId =
        hasSpecificCompany
            ? String(currentCompanyID)
            : "";

    const [loading, setLoading] =
        useState(true);

    const [err, setErr] =
        useState("");

    const [
        formError,
        setFormError,
    ] = useState("");

    const [formInit, setFormInit] =
        useState({});

    const [meta, setMeta] =
        useState({
            shops: [],
            models: [],
            countries: [],

            countryExtraDefinitions:
                [],

            countryExtraKeys: [],

            pricingMode: "FIXED",

            existingShopIds: [],

            availableShops: [],

            extras: [],
        });

    const initialSnapshotRef =
        useRef(null);

    const handleTabChange =
        useCallback((nextId) => {
            setFormError("");
            setActiveTab(nextId);

            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }, []);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                if (!supplierId) {
                    throw new Error(
                        "Please select a specific company before opening this motorcycle form."
                    );
                }

                setLoading(true);
                setErr("");
                setFormError("");

                if (isEdit) {
                    const response =
                        await API.get(
                            `/shop-motorcycles/model/${motorcycleModelId}`,
                            {
                                params: {
                                    supplierId,
                                },
                            }
                        );

                    const item =
                        response?.data
                            ?.data || {};

                    const responseMeta =
                        response?.data
                            ?.meta || {};

                    const pricingMode =
                        responseMeta?.pricingMode ||
                        "FIXED";

                    const seasonalPricing =
                        pricingMode ===
                        "SEASON";

                    const starts =
                        item?.ShopMotorcyclePrice ||
                        [];

                    const existingShopIds =
                        uniqueIds(
                            responseMeta
                                ?.existingShopIds
                                ?.length
                                ? responseMeta.existingShopIds
                                : item?.shops?.map(
                                    (
                                        shop
                                    ) =>
                                        shop.shopId
                                )
                        );

                    const init = {
                        ...item,

                        motorcycleModelId:
                            item?.motorcycleModelId ||
                            motorcycleModelId,

                        shopIds:
                            existingShopIds,

                        existingShopIds,

                        ShopMotorcycleAvailability:
                            normalizeAvailability(
                                item?.ShopMotorcycleAvailability
                            ),

                        rentalPolicy: {
                            ...(item?.RentalPolicy ||
                                item?.rentalPolicy ||
                                {}),
                        },

                        mileageServices:
                            buildMileageServicesInitialValues(
                                item?.ShopMotorcycleMileageExtra
                            ),

                        countryServices:
                            buildCountryServicesInitialValues(
                                item?.ShopMotorcycleCountryExtra
                            ),

                        prices:
                            pricesStartsToRanges(
                                starts,
                                seasonalPricing
                            ),

                        seasonalPricing,

                        _metaSeasonalFromData:
                            detectSeasonalPricing(
                                starts
                            ),

                        ui: {
                            overageEnabled:
                                !item
                                    ?.RentalPolicy
                                    ?.isUnlimitedMileage &&
                                Number(
                                    item
                                        ?.RentalPolicy
                                        ?.excessMileageFee ??
                                    0
                                ) >
                                0,

                            pricingUiMode:
                                "SIMPLE",
                        },
                    };

                    if (cancelled) {
                        return;
                    }

                    setMeta({
                        shops:
                            responseMeta?.shops ||
                            [],

                        models:
                            responseMeta?.models ||
                            [],

                        countries:
                            responseMeta?.countries ||
                            [],

                        countryExtraDefinitions:
                            responseMeta?.countryExtraDefinitions ||
                            [],

                        countryExtraKeys:
                            responseMeta?.countryExtraKeys ||
                            [],

                        pricingMode,

                        existingShopIds,

                        availableShops:
                            responseMeta?.availableShops ||
                            [],

                        extras:
                            responseMeta?.extras ||
                            responseMeta?.Extras ||
                            [],
                    });

                    setFormInit(init);

                    initialSnapshotRef.current =
                        init;

                    return;
                }

                const response =
                    await API.get(
                        "/shop-motorcycles/meta/form",
                        {
                            params: {
                                supplierId,
                            },
                        }
                    );

                const responseMeta =
                    response?.data?.meta ||
                    {};

                const init = {
                    shopIds: [],
                    existingShopIds: [],

                    motorcycleModelId:
                        "",

                    rentalPolicy: {},

                    mileageServices:
                        emptyMileageServices(),

                    countryServices:
                        createEmptyCountryServices(),

                    ShopMotorcycleExtra:
                        [],

                    ShopMotorcycleAvailability:
                        [],

                    prices: [],

                    seasonalPricing:
                        responseMeta?.pricingMode ===
                        "SEASON",

                    ui: {
                        overageEnabled:
                            false,

                        pricingUiMode:
                            "SIMPLE",
                    },
                };

                if (cancelled) {
                    return;
                }

                setMeta({
                    shops:
                        responseMeta?.shops ||
                        [],

                    models:
                        responseMeta?.models ||
                        [],

                    countries:
                        responseMeta?.countries ||
                        [],

                    countryExtraDefinitions:
                        responseMeta?.countryExtraDefinitions ||
                        [],

                    countryExtraKeys:
                        responseMeta?.countryExtraKeys ||
                        [],

                    pricingMode:
                        responseMeta?.pricingMode ||
                        "FIXED",

                    existingShopIds:
                        [],

                    availableShops:
                        responseMeta?.shops ||
                        [],

                    extras:
                        responseMeta?.extras ||
                        responseMeta?.Extras ||
                        [],
                });

                setFormInit(init);

                initialSnapshotRef.current =
                    init;
            } catch (error) {
                if (!cancelled) {
                    setErr(
                        error?.response
                            ?.data
                            ?.message ||
                        error?.message ||
                        "Failed to load"
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [
        isEdit,
        motorcycleModelId,
        supplierId,
    ]);

    const buildChangeSummary =
        useCallback((prev, cur) => {
            const seasonal =
                Boolean(
                    cur?.seasonalPricing
                );

            const priceKeys =
                seasonal
                    ? [
                        "start",
                        "end",
                        "low",
                        "medium",
                        "high",
                    ]
                    : [
                        "start",
                        "end",
                        "price",
                    ];

            const priceDiff =
                diffById(
                    prev?.prices,
                    cur?.prices,
                    priceKeys
                );

            const availDiff =
                diffById(
                    prev?.ShopMotorcycleAvailability,
                    cur?.ShopMotorcycleAvailability,
                    [
                        "startDate",
                        "endDate",
                    ]
                );

            const coreChanged =
                diffObject(
                    {
                        motorcycleModelId:
                            prev?.motorcycleModelId,

                        motorcycleYear:
                            prev?.motorcycleYear,

                        bestOffer:
                            prev?.bestOffer,

                        discountedDepositExtra1:
                            prev?.discountedDepositExtra1,

                        discountedDepositExtra2:
                            prev?.discountedDepositExtra2,

                        discountedDepositExtra3:
                            prev?.discountedDepositExtra3,

                        discountedDamageExcess1:
                            prev?.discountedDamageExcess1,

                        discountedDamageExcess2:
                            prev?.discountedDamageExcess2,

                        discountedDamageExcess3:
                            prev?.discountedDamageExcess3,

                        discountedTheftExcess1:
                            prev?.discountedTheftExcess1,

                        discountedTheftExcess2:
                            prev?.discountedTheftExcess2,

                        discountedTheftExcess3:
                            prev?.discountedTheftExcess3,

                        rentalPolicy:
                            prev?.rentalPolicy,

                        mileageServices:
                            prev?.mileageServices,

                        countryServices:
                            prev?.countryServices,

                        ShopMotorcycleExtra:
                            prev?.ShopMotorcycleExtra,
                    },

                    {
                        motorcycleModelId:
                            cur?.motorcycleModelId,

                        motorcycleYear:
                            cur?.motorcycleYear,

                        bestOffer:
                            cur?.bestOffer,

                        discountedDepositExtra1:
                            cur?.discountedDepositExtra1,

                        discountedDepositExtra2:
                            cur?.discountedDepositExtra2,

                        discountedDepositExtra3:
                            cur?.discountedDepositExtra3,

                        discountedDamageExcess1:
                            cur?.discountedDamageExcess1,

                        discountedDamageExcess2:
                            cur?.discountedDamageExcess2,

                        discountedDamageExcess3:
                            cur?.discountedDamageExcess3,

                        discountedTheftExcess1:
                            cur?.discountedTheftExcess1,

                        discountedTheftExcess2:
                            cur?.discountedTheftExcess2,

                        discountedTheftExcess3:
                            cur?.discountedTheftExcess3,

                        rentalPolicy:
                            cur?.rentalPolicy,

                        mileageServices:
                            cur?.mileageServices,

                        countryServices:
                            cur?.countryServices,

                        ShopMotorcycleExtra:
                            cur?.ShopMotorcycleExtra,
                    }
                );

            const previousShopIds =
                uniqueIds(
                    prev?.shopIds
                ).sort();

            const currentShopIds =
                uniqueIds(
                    cur?.shopIds
                ).sort();

            const shopsChanged =
                JSON.stringify(
                    previousShopIds
                ) !==
                JSON.stringify(
                    currentShopIds
                );

            return {
                changed:
                    priceDiff.changed ||
                    availDiff.changed ||
                    coreChanged.length >
                    0 ||
                    shopsChanged,

                priceDiff,
                availDiff,
                coreChanged,
                shopsChanged,
            };
        }, []);

    const validateCurrentTab =
        useCallback((
            tabId,
            values
        ) => {
            if (tabId === "detail") {
                if (
                    !values.motorcycleModelId
                ) {
                    return "Please select a motorcycle model.";
                }

                if (
                    !uniqueIds(
                        values.shopIds
                    ).length
                ) {
                    return "Please select at least one shop.";
                }

                const minAge =
                    values.rentalPolicy
                        ?.minAge;

                if (
                    minAge !== "" &&
                    minAge !== null &&
                    minAge !== undefined &&
                    (!Number.isFinite(
                        Number(minAge)
                    ) ||
                        Number(minAge) < 0)
                ) {
                    return "Minimum rider age must be 0 or greater.";
                }

                const minExp =
                    values.rentalPolicy
                        ?.minExp;

                if (
                    minExp !== "" &&
                    minExp !== null &&
                    minExp !== undefined &&
                    (!Number.isFinite(
                        Number(minExp)
                    ) ||
                        Number(minExp) < 0)
                ) {
                    return "Required riding experience must be 0 or greater.";
                }

                return "";
            }

            if (tabId === "travel") {
                return (
                    validateCountryServices(
                        values.countryServices,
                        getInternationalTravelAllowed(
                            values
                        )
                    ) || ""
                );
            }

            if (
                tabId ===
                "excess-deposit"
            ) {
                return (
                    validateExcessDeposit(
                        values
                    ) || ""
                );
            }

            return "";
        }, []);

    const handleNext =
        useCallback((values) => {
            const validationError =
                validateCurrentTab(
                    activeTab,
                    values
                );

            if (validationError) {
                setFormError(
                    validationError
                );

                return;
            }

            setFormError("");

            if (
                currentIndex >= 0 &&
                currentIndex <
                tabs.length - 1
            ) {
                handleTabChange(
                    tabs[currentIndex + 1]
                        .id
                );
            }
        }, [
            activeTab,
            currentIndex,
            handleTabChange,
            tabs,
            validateCurrentTab,
        ]);

    const handlePrevious =
        useCallback(() => {
            if (currentIndex > 0) {
                handleTabChange(
                    tabs[currentIndex - 1]
                        .id
                );
            }
        }, [
            currentIndex,
            handleTabChange,
            tabs,
        ]);

    const buildPayload =
        useCallback((
            values
        ) => {
            const seasonal =
                meta?.pricingMode ===
                "SEASON";

            const normalizedPrices =
                normalizeRangesSave(
                    values.prices,
                    seasonal
                ) || [];

            const internationalTravelAllowed =
                getInternationalTravelAllowed(
                    values
                );

            return {
                motorcycleYear: Number(
                    values.motorcycleYear ||
                    new Date().getFullYear()
                ),

                bestOffer: Boolean(
                    values.bestOffer
                ),

                discountedDepositExtra1:
                    numOrZero(
                        values.discountedDepositExtra1
                    ),

                discountedDepositExtra2:
                    numOrZero(
                        values.discountedDepositExtra2
                    ),

                discountedDepositExtra3:
                    numOrZero(
                        values.discountedDepositExtra3
                    ),

                discountedDamageExcess1:
                    numOrZero(
                        values.discountedDamageExcess1
                    ),

                discountedDamageExcess2:
                    numOrZero(
                        values.discountedDamageExcess2
                    ),

                discountedDamageExcess3:
                    numOrZero(
                        values.discountedDamageExcess3
                    ),

                discountedTheftExcess1:
                    numOrZero(
                        values.discountedTheftExcess1
                    ),

                discountedTheftExcess2:
                    numOrZero(
                        values.discountedTheftExcess2
                    ),

                discountedTheftExcess3:
                    numOrZero(
                        values.discountedTheftExcess3
                    ),

                prices: rangesToStarts(
                    normalizedPrices,
                    seasonal
                ),

                /*
                 * 当前没有不可用日期 UI，
                 * 但保留已有字段，避免编辑保存时意外删除后端数据。
                 */
                availability:
                    (
                        values.ShopMotorcycleAvailability ||
                        []
                    ).map((item) => ({
                        startDate:
                            item.startDate,

                        endDate:
                            item.endDate,
                    })),

                mileageExtras:
                    buildMileageExtrasPayload(
                        values.mileageServices
                    ),

                countryExtras:
                    buildCountryExtrasPayload(
                        values.countryServices,
                        internationalTravelAllowed
                    ),

                extras:
                    buildRegularExtrasPayload(
                        values.ShopMotorcycleExtra
                    ),

                rentalPolicy: {
                    minAge: numOrNull(
                        values.rentalPolicy
                            ?.minAge
                    ),

                    minExp: numOrNull(
                        values.rentalPolicy
                            ?.minExp
                    ),

                    licenseType:
                        values.rentalPolicy
                            ?.licenseType ??
                        "A",

                    isUnlimitedMileage:
                        Boolean(
                            values.rentalPolicy
                                ?.isUnlimitedMileage
                        ),

                    mileageLimit:
                        numOrNull(
                            values.rentalPolicy
                                ?.mileageLimit
                        ),

                    excessMileageFee:
                        numOrNull(
                            values.rentalPolicy
                                ?.excessMileageFee
                        ),

                    countryRestriction:
                        internationalTravelAllowed,

                    canLeaveCity:
                        Boolean(
                            values.rentalPolicy
                                ?.canLeaveCity
                        ),

                    canLeaveState:
                        Boolean(
                            values.rentalPolicy
                                ?.canLeaveState
                        ),

                    deposit: numOrNull(
                        values.rentalPolicy
                            ?.deposit
                    ),

                    damageExcess:
                        numOrNull(
                            values.rentalPolicy
                                ?.damageExcess
                        ),

                    theftExcess:
                        numOrNull(
                            values.rentalPolicy
                                ?.theftExcess
                        ),
                },
            };
        }, [meta?.pricingMode]);

    const handleSubmit =
        useCallback(async (
            values,
            { setSubmitting }
        ) => {
            try {
                setFormError("");

                const selectedShopIds =
                    uniqueIds(
                        values.shopIds
                    );

                const detailError =
                    validateCurrentTab(
                        "detail",
                        values
                    );

                if (detailError) {
                    setActiveTab(
                        "detail"
                    );

                    setFormError(
                        detailError
                    );

                    window.scrollTo({
                        top: 0,
                        behavior: "smooth",
                    });

                    return;
                }

                const travelError =
                    validateCurrentTab(
                        "travel",
                        values
                    );

                if (travelError) {
                    setActiveTab(
                        "travel"
                    );

                    setFormError(
                        travelError
                    );

                    window.scrollTo({
                        top: 0,
                        behavior: "smooth",
                    });

                    return;
                }

                const excessDepositError =
                    validateCurrentTab(
                        "excess-deposit",
                        values
                    );

                if (
                    excessDepositError
                ) {
                    setActiveTab(
                        "excess-deposit"
                    );

                    setFormError(
                        excessDepositError
                    );

                    window.scrollTo({
                        top: 0,
                        behavior: "smooth",
                    });

                    return;
                }

                const summary =
                    buildChangeSummary(
                        initialSnapshotRef.current ||
                        {},
                        values
                    );

                if (!summary.changed) {
                    setFormError(
                        "No changes to save."
                    );

                    return;
                }

                const payload = {
                    ...buildPayload(values),
                    shopIds: selectedShopIds,
                };

                if (isEdit) {
                    const response =
                        await API.put(
                            `/shop-motorcycles/model/${motorcycleModelId}`,
                            payload,
                            {
                                params: {
                                    supplierId,
                                },
                            }
                        );

                    const result =
                        response?.data || {};

                    const nextSnapshot = {
                        ...values,
                        shopIds:
                            selectedShopIds,
                        existingShopIds:
                            selectedShopIds,
                    };

                    initialSnapshotRef.current =
                        nextSnapshot;

                    setFormInit(
                        nextSnapshot
                    );

                    setMeta(
                        (previous) => ({
                            ...previous,
                            existingShopIds:
                                selectedShopIds,
                            availableShops:
                                (previous.shops || []).filter(
                                    (shop) =>
                                        !selectedShopIds.includes(
                                            String(shop.id)
                                        )
                                ),
                        })
                    );

                    const createdCount =
                        Number(result.createdCount || 0);

                    const restoredCount =
                        Number(result.restoredCount || 0);

                    const deactivatedCount =
                        Number(result.deactivatedCount || 0);

                    const details = [
                        createdCount > 0
                            ? `${createdCount} created`
                            : "",
                        restoredCount > 0
                            ? `${restoredCount} restored`
                            : "",
                        deactivatedCount > 0
                            ? `${deactivatedCount} deactivated`
                            : "",
                    ].filter(Boolean);

                    alert(
                        details.length
                            ? `Saved: ${details.join(", ")}.`
                            : "Saved"
                    );

                    return;
                }

                const response =
                    await API.post(
                        "/shop-motorcycles/model",
                        {
                            ...payload,
                            motorcycleModelId:
                                values.motorcycleModelId,
                        },
                        {
                            params: {
                                supplierId,
                            },
                        }
                    );

                const result =
                    response?.data || {};

                const nextSnapshot = {
                    ...values,
                    shopIds:
                        selectedShopIds,
                    existingShopIds:
                        selectedShopIds,
                };

                initialSnapshotRef.current =
                    nextSnapshot;

                setFormInit(
                    nextSnapshot
                );

                const createdCount =
                    Number(result.createdCount || 0);

                const restoredCount =
                    Number(result.restoredCount || 0);

                const overwrittenCount =
                    Number(result.overwrittenCount || 0);

                const details = [
                    createdCount > 0
                        ? `${createdCount} created`
                        : "",
                    restoredCount > 0
                        ? `${restoredCount} restored`
                        : "",
                    overwrittenCount > 0
                        ? `${overwrittenCount} updated`
                        : "",
                ].filter(Boolean);

                alert(
                    details.length
                        ? `Saved: ${details.join(", ")}.`
                        : "Created"
                );
            } catch (error) {
                const message =
                    Array.isArray(
                        error?.response
                            ?.data?.message
                    )
                        ? error.response.data.message.join(
                            ", "
                        )
                        : error?.response
                            ?.data
                            ?.message ||
                        error?.message ||
                        "Submit failed";

                setFormError(message);

                window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });
            } finally {
                setSubmitting(false);
            }
        }, [
            buildChangeSummary,
            buildPayload,
            isEdit,
            motorcycleModelId,
            supplierId,
            validateCurrentTab,
        ]);

    if (loading) {
        return (
            <div className="p-4">
                Loading…
            </div>
        );
    }

    if (err) {
        return (
            <div className="p-4 text-danger">
                {err}
            </div>
        );
    }

    const activeTabLabel =
        tabs[currentIndex]?.label ||
        "";

    return (
        <Formik
            enableReinitialize
            initialValues={formInit}
            onSubmit={handleSubmit}
            validateOnChange={false}
            validateOnBlur
        >
            {(formik) => (
                <Container
                    fluid
                    className="d-flex flex-column"
                >
                    <Row className="justify-content-center">
                        <Col
                            sm={12}
                            className="mb-4"
                        >
                            <span className="fw-semibold">
                                Motorcycle
                                Information
                            </span>
                        </Col>

                        <Col
                            sm="auto"
                            style={{
                                position:
                                    "sticky",

                                top: "10px",

                                height:
                                    "fit-content",

                                alignSelf:
                                    "flex-start",

                                width:
                                    "260px",

                                maxWidth:
                                    "280px",
                            }}
                        >
                            <Card>
                                <Nav
                                    variant="pills"
                                    className="flex-column p-3"
                                >
                                    {tabs.map(
                                        (
                                            tab
                                        ) => (
                                            <Nav.Item
                                                key={
                                                    tab.id
                                                }
                                            >
                                                <Nav.Link
                                                    active={
                                                        activeTab ===
                                                        tab.id
                                                    }
                                                    onClick={() =>
                                                        handleTabChange(
                                                            tab.id
                                                        )
                                                    }
                                                    className="
                                                        d-flex
                                                        align-items-center
                                                        mb-2
                                                        rounded
                                                    "
                                                >
                                                    {
                                                        tab.label
                                                    }
                                                </Nav.Link>
                                            </Nav.Item>
                                        )
                                    )}
                                </Nav>
                            </Card>
                        </Col>

                        <Col className="flex-grow-1">
                            <Card className="p-4">
                                <div className="mb-4 pb-3 border-bottom">
                                    <h5 className="mb-0 fw-semibold">
                                        {activeTabLabel}
                                    </h5>
                                </div>

                                {formError && (
                                    <Alert
                                        variant="danger"
                                        className="mb-4"
                                        dismissible
                                        onClose={() =>
                                            setFormError(
                                                ""
                                            )
                                        }
                                    >
                                        {
                                            formError
                                        }
                                    </Alert>
                                )}

                                {activeTab ===
                                    "detail" && (
                                        <DetailTab
                                            meta={
                                                meta
                                            }
                                            isEdit={
                                                isEdit
                                            }
                                        />
                                    )}

                                {activeTab ===
                                    "travel" && (
                                        <TravelTab
                                            meta={
                                                meta
                                            }
                                        />
                                    )}

                                {activeTab ===
                                    "mileage" && (
                                        <MileageTab />
                                    )}

                                {activeTab ===
                                    "excess-deposit" && (
                                        <ExcessDepositTab
                                            meta={
                                                meta
                                            }
                                        />
                                    )}

                                {activeTab ===
                                    "prices" && (
                                        <PricingTab
                                            meta={
                                                meta
                                            }
                                        />
                                    )}

                                <div
                                    className="
                                        d-flex
                                        justify-content-between
                                        align-items-center
                                        flex-wrap
                                        gap-3
                                        mt-4
                                        pt-3
                                        border-top
                                    "
                                >
                                    <div>
                                        {currentIndex >
                                            0 && (
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    onClick={
                                                        handlePrevious
                                                    }
                                                    disabled={
                                                        formik.isSubmitting
                                                    }
                                                >
                                                    Previous
                                                </Button>
                                            )}
                                    </div>

                                    <div className="d-flex flex-wrap gap-2">
                                        <Button
                                            type="button"
                                            variant="success"
                                            onClick={
                                                formik.handleSubmit
                                            }
                                            disabled={
                                                formik.isSubmitting
                                            }
                                        >
                                            {formik.isSubmitting
                                                ? "Saving…"
                                                : isEdit
                                                    ? "Save changes"
                                                    : "Create motorcycle"}
                                        </Button>

                                        {currentIndex <
                                            tabs.length -
                                            1 && (
                                                <Button
                                                    type="button"
                                                    onClick={() =>
                                                        handleNext(
                                                            formik.values
                                                        )
                                                    }
                                                    disabled={
                                                        formik.isSubmitting
                                                    }
                                                >
                                                    Next
                                                </Button>
                                            )}
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            )}
        </Formik>
    );
}